import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { AttachmentTemplatesTable } from '../-components/AttachmentTemplatesTable';
import { adminAttachmentTemplatesQueryOptions } from '../-hooks/useAdminAttachmentTemplates';
import { useAttachmentTemplatesPage } from '../-hooks/useAttachmentTemplatesPage';
import { MAX_COUNT } from '../-utils/listParams';

const attachmentTemplatesSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  /**
   * Clamped, not rejected. The table footer offers 50 / 100 / 500 and the
   * route's `count` caps at 100, so picking 500 would 400 the whole list —
   * this snaps it back to the server's maximum instead.
   */
  limit: fallback(
    z
      .number()
      .int()
      .transform(v => Math.min(Math.max(v, 10), MAX_COUNT)),
    50,
  ),
  search: z.string().optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
  // No `sort`: the endpoint declares no such parameter and always orders by
  // UPDATED_AT DESC, so accepting one would put a dead value in the URL.
});

export const Route = createFileRoute(
  '/management/attachment-templates/(pages)/',
)({
  validateSearch: attachmentTemplatesSearch,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.search !== undefined ? { search: search.search } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // Swallowed on purpose: the loader is a prefetch, and letting it reject
  // hands the route to the router's error boundary, which replaces the page
  // and hides the table's own "could not load" banner. Matches the other
  // management lists; the component's `useQuery` surfaces it inline.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminAttachmentTemplatesQueryOptions(deps))
      .catch(() => undefined),
  component: AttachmentTemplatesPage,
});

function AttachmentTemplatesPage() {
  const page = useAttachmentTemplatesPage();

  return (
    <PageContainer>
      <AttachmentTemplatesTable page={page} />
    </PageContainer>
  );
}
